import HCaptchaField from '@atoms/HCaptchaField/HCaptchaField';
import TextArea from '@atoms/TextArea/TextArea';
import TextInput from '@atoms/TextInput/TextInput';
import { IContactFormOption, IContactFormProps } from '@models/IContactUsForm';
import { IFilter } from '@models/IFilterGroup';
import IFormValues from '@models/IFormValues';
import CheckboxFormField from '@molecules/CheckboxFormField/CheckboxFormField';
import ContactFormWrapper from '@molecules/ContactFormWrapper/ContactFormWrapper';
import PersonalDetailsFormFields from '@molecules/PersonalDetailsFormFields/PersonalDetailsFormFields';
import SelectFormField from '@molecules/SelectFormField/SelectFormField';
import { getCookie } from '@utility/helpers/cookieUtils';
import useContactForm from 'hooks/useContactForm';
import React, { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

const mapOptions = (options: IContactFormOption[]): IFilter[] =>
  options.map(({ value, label }) => ({ id: value, label }));

const ContactForm: React.FC<IContactFormProps> = ({
  text,
  formFields,
  endpoint,
  guid,
  language,
  hCaptchaSiteKey,
}) => {
  const [vuid, setVuid] = useState('');
  const { formState, onSubmit } = useContactForm(endpoint);

  useEffect(() => setVuid(getCookie('vuid') ?? ''), []);

  const handleFormSubmit: SubmitHandler<IFormValues> = async (data) => {
    onSubmit({
      contactBlockGuid: guid,
      language,
      vuid,
      ...data,
    });
  };

  return (
    <ContactFormWrapper
      text={text}
      step={formState}
      registerOptions={{
        defaultValues: {
          firstName: '',
          lastName: '',
          email: '',
          telephoneNumber: '',
          telephoneCountryCode: '',
          country: '',
          companyName: '',
          industry: '',
          enquiryType: '',
          message: '',
          marketingEmailsSignup: false,
          privacyPolicyConsent: false,
          captchaToken: '',
        },
      }}
      onSubmit={handleFormSubmit}
    >
      <PersonalDetailsFormFields
        {...formFields}
        requiredMessage={text.validationRequired}
        emailValidationMessage={text.validationEmail}
      />
      <div className="tw-col-span-2">
        <SelectFormField
          name="country"
          label={formFields.country.label}
          requiredMessage={text.validationRequired}
          placeholder={formFields.country.placeholder}
          defaultValue={formFields.country.preSelectedValue}
          options={mapOptions(formFields.country.options)}
        />
      </div>
      <div className="tw-col-span-2">
        <TextInput
          id="region"
          className="tw-text-sm"
          label={formFields.region.label}
          placeholder={formFields.region.placeholder}
        />
      </div>
      <div className="tw-col-span-2 md:tw-col-span-1">
        <TextInput
          id="companyName"
          className="tw-text-sm"
          label={formFields.companyName.label}
          placeholder={formFields.companyName.placeholder}
        />
      </div>
      <div className="tw-col-span-2 md:tw-col-span-1">
        <SelectFormField
          name="industry"
          label={formFields.industry.label}
          placeholder={formFields.industry.placeholder}
          options={mapOptions(formFields.industry.options)}
        />
      </div>
      <div className="tw-col-span-2">
        <SelectFormField
          name="enquiryType"
          label={formFields.enquiryType.label}
          placeholder={formFields.enquiryType.placeholder}
          defaultValue={formFields.enquiryType.preSelectedValue}
          options={mapOptions(formFields.enquiryType.options)}
        />
      </div>
      <div className="tw-col-span-2">
        <TextArea
          id="message"
          label={formFields.message.label}
          placeholder={formFields.message.placeholder}
          registerOptions={{
            required: text.validationRequired,
          }}
          name="message"
          className="tw-min-h-32"
          maxLength={300}
        />
      </div>
      <div className="tw-col-span-2 tw-grid tw-gap-6">
        {!!hCaptchaSiteKey && (
          <HCaptchaField
            name="captchaToken"
            siteKey={hCaptchaSiteKey}
            registerOptions={{ required: text.validationRequired }}
          />
        )}
        <CheckboxFormField
          name="marketingEmailsSignup"
          label={formFields.newsletterSignupLabel}
        />
        <CheckboxFormField
          name="privacyPolicyConsent"
          label={formFields.privacyPolicyLabel}
          registerOptions={{ required: text.validationRequired }}
        />
      </div>
    </ContactFormWrapper>
  );
};

export default ContactForm;
